/**
 * Pricing settings that live on the document.
 *
 * Deliberately dependency free so the document schema can import it without
 * creating a cycle. The math that turns a mockup into a quote lives in
 * lib/pricing.ts.
 *
 * The rule from the field: a quote shows LINE ITEM TOTALS, never the rate.
 * The rates live in here and in the settings panel, not in the customer's hands.
 */

export type BillingUnit = 'ft' | 'strand' | 'frond' | 'each'

export type RateKey =
  | 'roofPerFoot'
  | 'roofHardPerFoot'
  | 'monumentPerFoot'
  | 'garlandPerFoot'
  | 'treeStrand'
  | 'bushStrand'
  | 'frondEach'
  | 'clusterEach'
  | 'wreath2'
  | 'wreath3'
  | 'wreath4'
  | 'wreath6'

export interface RateDef {
  key: RateKey
  label: string
  unit: BillingUnit
  /** Shown in the picker so lines can be regrouped without hunting. */
  group: 'Linear' | 'Strands' | 'Each'
}

export const RATES: Record<RateKey, RateDef> = {
  roofPerFoot: { key: 'roofPerFoot', label: 'Roofline', unit: 'ft', group: 'Linear' },
  roofHardPerFoot: {
    key: 'roofHardPerFoot',
    label: 'Difficult roof',
    unit: 'ft',
    group: 'Linear',
  },
  monumentPerFoot: { key: 'monumentPerFoot', label: 'Monument', unit: 'ft', group: 'Linear' },
  garlandPerFoot: { key: 'garlandPerFoot', label: 'Garland', unit: 'ft', group: 'Linear' },
  treeStrand: { key: 'treeStrand', label: 'Tree / palm', unit: 'strand', group: 'Strands' },
  bushStrand: { key: 'bushStrand', label: 'Bush / hedge', unit: 'strand', group: 'Strands' },
  frondEach: { key: 'frondEach', label: 'Palm fronds', unit: 'frond', group: 'Each' },
  clusterEach: { key: 'clusterEach', label: 'Hanging cluster', unit: 'each', group: 'Each' },
  wreath2: { key: 'wreath2', label: 'Wreath, 2 ft', unit: 'each', group: 'Each' },
  wreath3: { key: 'wreath3', label: 'Wreath, 3 ft', unit: 'each', group: 'Each' },
  wreath4: { key: 'wreath4', label: 'Wreath, 4 ft', unit: 'each', group: 'Each' },
  wreath6: { key: 'wreath6', label: 'Wreath, 6 ft', unit: 'each', group: 'Each' },
}

export const RATE_ORDER: RateKey[] = [
  'roofPerFoot',
  'roofHardPerFoot',
  'monumentPerFoot',
  'garlandPerFoot',
  'treeStrand',
  'bushStrand',
  'frondEach',
  'clusterEach',
  'wreath2',
  'wreath3',
  'wreath4',
  'wreath6',
]

export type RateCard = Record<RateKey, number>

/**
 * PLACEHOLDERS, AND THEY HAVE TO STAY PLACEHOLDERS.
 *
 * This used to be one lighting company's real 2026 rate card, shipped as the
 * app's defaults. That is somebody's pricing going out in the bundle to every
 * competitor who installs this, and it was written into every document saved.
 *
 * Nothing reads these any more: the quote is typed now, and its starting
 * prices are the trade's public ranges in lib/quote.ts, not one shop's card.
 * The keys survive only because documents carry a pricing block and removing
 * it is a schema migration. Whoever does that migration should delete this
 * file. Nobody should ever put real numbers back in it. Every rate is one
 * dollar, which is transparently not a price and keeps the old machinery
 * arithmetically alive for the tests that still walk it.
 */
export const defaultRates = (): RateCard => ({
  roofPerFoot: 1,
  roofHardPerFoot: 1,
  monumentPerFoot: 1,
  garlandPerFoot: 1,
  treeStrand: 1,
  bushStrand: 1,
  frondEach: 1,
  clusterEach: 1,
  wreath2: 1,
  wreath3: 1,
  wreath4: 1,
  wreath6: 1,
})

export type SeasonTier = 'sep' | 'oct' | 'nov'

export interface TierDef {
  key: SeasonTier
  label: string
  discountPct: number
}

/** Early booking is the close. Show all three so the customer can see the cost of waiting. */
export const TIERS: TierDef[] = [
  { key: 'sep', label: 'September install', discountPct: 15 },
  { key: 'oct', label: 'October install', discountPct: 10 },
  { key: 'nov', label: 'November install', discountPct: 0 },
]

export const tierDef = (t: SeasonTier): TierDef => TIERS.find((x) => x.key === t) ?? TIERS[2]

/** A line the user has taken control of. Anything unset stays derived. */
export interface LineOverride {
  rateKey?: RateKey
  qty?: number
  rate?: number
  label?: string
  excluded?: boolean
}

/** One-off work with no geometry behind it: a flag pole, a candy cane run. */
export interface CustomLine {
  id: string
  label: string
  qty: number
  rate: number
}

export interface PricingSettings {
  /** Rates are a company setting, not a per-photo one, but they live on the
   *  document so a saved quote still reprices exactly as it was sold. */
  rates: RateCard
  tier: SeasonTier
  /** How many lights on a strand. Drives strand counts on trunks and bushes. */
  lightsPerStrand: number
  /** Keyed by line key, not layer id, so a palm's trunk and fronds differ. */
  overrides: Record<string, LineOverride>
  extras: CustomLine[]
}

export const defaultPricing = (): PricingSettings => ({
  rates: defaultRates(),
  tier: 'sep',
  lightsPerStrand: 100,
  overrides: {},
  extras: [],
})

